import { Link } from '@design-system/Link';
import { Button } from '@design-system/Button';
import { FormulaCard } from '@design-system/Cards/FormulaCard';
import { SHOP_PATH, SMP_PATH } from '@/constants/paths';
import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import { useNutritionViewModal } from '@/features/NutritionsViewModels/NutritionProducts.view.model';
import { Render } from '@/components/Helpers/ComponentRender/Render';
import { getMyIngredientsPageUrl } from '@/utils/nutrition/getMyIngredientsPageUrl';
import { FoodProduct } from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { useBootup } from '@/hooks/useBootup';
import { NutritionPageCommonSkeleton } from '../../NutritionPage/NutritionPageCommonSkeleton';
import { cn } from '@/utils/styles';
import { EventTracker } from '@/utils';

export const HomePageFormulaSection = () => {
  const { foodFormulaData, isFoodFormulaLoading, isErrorInFormula } =
    useNutritionViewModal();
  const { bootupState } = useBootup();
  const kitType = bootupState?.kitType;
  const isFormulasSubscribed = foodFormulaData?.some(
    (item) => item.is_subscribed,
   );

  if (isErrorInFormula) {
    return;
  }

  const handleProductRedirection = (product: FoodProduct): string => {
    EventTracker.trackEvent(
      'home',
      `home_myFormulas_${product.name.toLocaleLowerCase()}`,
    );
    return getMyIngredientsPageUrl({ product: product.name });
  };

  const handleOrderFormulasRedirection = () => {
    EventTracker.trackEvent('home', 'home_myFormulas_orderFormulas');
    return SHOP_PATH;
  };

  const handleManageFormulasRedirection = () => {
    EventTracker.trackEvent('home', 'home_preRR_formulas_manageSubscription');
    return SMP_PATH;
  };

  const getDescriptionBasedOnKitType = (kitType: string) => {
    switch (kitType) {
      case 'OHI':
        return 'Accelerate your path to optimal oral health with ingredients that support your oral microbiome and target scores that need improvement.';
      case 'GI':
        return 'Accelerate your path to optimal gut health with supplements and biotics that fill nutrient gaps and target scores that need improvement.';
      case 'FBI':
        return 'Accelerate your path to better health with supplements and biotics that fill nutrient gaps and target scores that need improvement.';
      default:
        return 'Accelerate your path to better health with supplements and biotics that fill nutrient gaps and target scores that need improvement.';
    }
  };

  if (isFoodFormulaLoading) {
    return (
      <div className="page-container">
        <div className="flex flex-col gap-4 py-6">
          <NutritionPageCommonSkeleton className="h-52 max-w-xl w-[10.5rem]" />
        </div>
      </div>
    );
  }

  if (isErrorInFormula || !foodFormulaData || foodFormulaData.length === 0) {
    return null;
  }

  return (
    <Render when={foodFormulaData && foodFormulaData?.length > 0}>
      <div className="page-container flex flex-col">
        <p className="mb-2 typography-h1 text-primary flex flex-row items-center gap-2">
          {bootupState?.isEligibleForSupplements
            ? 'My Formulas'
            : 'My Supplements & Biotics'}
        </p>

        <p className="mb-6 typography-body text-tertiary lg:w-[35rem]">
          {getDescriptionBasedOnKitType(kitType ?? 'FBI')}
        </p>

        <div
          className={cn(
            'w-full grid grid-cols-2 gap-2',
            'lg:grid lg:grid-cols-4 lg:gap-2',
            'lg:pb-4',
          )}
        >
          {foodFormulaData?.map((product) => (
            <Link
              variant="flat"
              to={handleProductRedirection(product)}
              className="w-full"
            >
              <FormulaCard
                title={product.name}
                formulaSrc={product.image_url}
                isSubscribed={product.is_subscribed}
                ingredientsCount={product.ingredients_count}
                isCustom={!bootupState?.isEligibleForSupplements}
                className="w-full h-full"
              />
            </Link>
          ))}
        </div>

        {bootupState?.isEligibleForSupplements &&
          (isFormulasSubscribed ? (
            <Button
              asChild
              variant="secondary"
              className="w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
            >
              <Link to={handleManageFormulasRedirection()}>
                Manage my subscription
                <ChevronRightIcon height={24} width={24} />
              </Link>
            </Button>
          ) : (
            <Button
              asChild
              variant="outline"
              className="dark bg-overlay-black w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
            >
              <Link to={handleOrderFormulasRedirection()}>
                Order my formulas
                <ChevronRightIcon height={24} width={24} />
              </Link>
            </Button>
          ))
        }
      </div>
    </Render>
  );
};
